import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  Text,
  Textarea,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
  VStack,
  Badge,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { startSession, endSession, answerNps } from "../../actions/user/userActions";

export default function NpsPopUp() {
  const dispatch = useDispatch();
  const toast = useToast();

  // ------------ TOP-LEVEL HOOKS (MUST REMAIN TOP LEVEL) ------------
  const user = useSelector((s) => s.userReducer?.data);
  const userId = user?._id || user?.id || user?.user || user?.userId || null;

  // refs
  const sessionIdRef = useRef(null);
  const startedAtRef = useRef(null);
  const surveyIdRef = useRef(null);
  const autoEndTimerRef = useRef(null);

  // NEW: mark if the user completed the current pending survey (prevents reopening)
  const surveyCompletedRef = useRef(false);

  // UI state
  const [showModal, setShowModal] = useState(false);
  const [mainQuestion, setMainQuestion] = useState("");
  const [selectedScore, setSelectedScore] = useState(null);
  const [followupQuestion, setFollowupQuestion] = useState("");
  const [followupAnswer, setFollowupAnswer] = useState("");
  const [loadingScore, setLoadingScore] = useState(false);
  const [loadingFollowup, setLoadingFollowup] = useState(false);

  // ADDED: Capture all survey data
  const [surveyData, setSurveyData] = useState({
    mainScore: null,
    followupBucket: "",
    completedAt: null,
    followupAnswer: ""
  });

  // ------------ CALLBACKS ------------
  const startUserSession = useCallback(
    async (opts = {}) => {
      if (!userId) return null;
      try {
        const resp = await dispatch(
          startSession(userId, {
            userId,
            startedAt: opts.startedAt || new Date().toISOString(),
          })
        );

        const sid = resp?._id || resp?.data?._id || resp?.id || null;
        sessionIdRef.current = sid;
        startedAtRef.current = resp?.startedAt || resp?.data?.startedAt || new Date().toISOString();

        console.info("[NPS] session started:", sid);

        // Auto-end session after 10 minutes
        clearTimeout(autoEndTimerRef.current);
        autoEndTimerRef.current = setTimeout(() => {
          console.info("[NPS] Auto-ending session after 10 minutes");
          endUserSession();
        }, 10 * 60 * 1000); // 600000 ms = 10 minutes

        return resp;
      } catch (err) {
        console.error("[NPS] startSession failed:", err);
        return null;
      }
    },
    [dispatch, userId]
  );

  const endUserSession = useCallback(
    async (opts = {}) => {
      if (!userId || !sessionIdRef.current) return null;
      try {
        const endedAt = opts.endedAt || new Date().toISOString();
        const payload = {
          userId,
          sessionId: sessionIdRef.current,
          endedAt,
          id: sessionIdRef.current,
          _id: sessionIdRef.current,
          session_id: sessionIdRef.current,
        };

        console.info("[NPS] Ending session with payload:", payload);
        const resp = await dispatch(endSession(userId, payload));
        console.info("[NPS] endSession result:", resp);

        // Wait for backend to create any pending survey, then check once
        setTimeout(() => {
          console.info("[NPS] Checking for survey after session end...");
          checkPendingSurvey();
        }, 2000);

        return resp;
      } catch (err) {
        console.error("[NPS] endSession failed:", err);
        return null;
      }
    },
    [dispatch, userId]
  );

  const checkPendingSurvey = useCallback(async () => {
    if (!userId) {
      console.info("[NPS] No userId, skipping survey check");
      return null;
    }

    // If we already completed the most recent survey, do not re-open
    if (surveyCompletedRef.current) {
      console.info("[NPS] surveyCompletedRef true — skipping pending check");
      return null;
    }

    try {
      console.info("[NPS] Checking pending survey for user:", userId);
      const resp = await fetch(`/api/v1/nps/pending?userId=${userId}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include"
      });

      // IMPORTANT: do NOT auto-retry on 204 — this caused repeated modal openings
      if (resp.status === 204) {
        console.info("[NPS] no pending survey (204) - not retrying automatically");
        return null;
      }

      if (!resp.ok) {
        const txt = await resp.text();
        console.warn("[NPS] pending returned non-ok:", resp.status, txt);
        return null;
      }

      const data = await resp.json();
      console.info("[NPS] Survey data received:", data);

      surveyIdRef.current = data?.surveyId || data?._id || data?.id;
      setMainQuestion(
        data?.mainQuestion ||
          data?.main_question ||
          "On a scale from 0 to 10, how likely are you to recommend our product to a friend or colleague?"
      );

      if (surveyIdRef.current) {
        // If we already completed a survey in this session, don't immediately reopen
        if (surveyCompletedRef.current) {
          console.info("[NPS] survey already completed; skipping modal show");
          return null;
        }

        setShowModal(true);
        console.info("[NPS] pending survey found:", surveyIdRef.current);
        toast({
          title: "Survey Ready!",
          description: "Please rate your experience",
          status: "success",
          duration: 3000,
        });
      } else {
        console.warn("[NPS] pending returned but no surveyId found:", data);
      }

      return data;
    } catch (err) {
      console.error("[NPS] checkPendingSurvey error:", err);
      return null;
    }
  }, [userId, toast]);

  // helper to compute bucket/question
  const getBucketFromScore = useCallback((score) => {
    if (score <= 6) return "detractor";
    if (score <= 8) return "passive";
    return "promoter";
  }, []);

  const getFollowupQuestionFromBucket = useCallback((bucket) => {
    const questions = {
      detractor: "What was missing or disappointing in your experience?",
      passive: "What's one feature or improvement that would make you rate us higher?",
      promoter: "Would you be interested in joining a customer advocacy program (e.g., case study, testimonial, referral program)?"
    };
    return questions[bucket] || "Thank you for your feedback. Is there anything else you'd like to share?";
  }, []);

  const getBucketColor = useCallback((bucket) => {
    const colors = {
      detractor: "red",
      passive: "orange",
      promoter: "green"
    };
    return colors[bucket] || "gray";
  }, []);

  // UPDATED: submitScore to capture all backend fields AND NOT trigger re-open
  const submitScore = useCallback(
    async (score) => {
      if (!userId || !surveyIdRef.current) {
        toast({ title: "Missing survey or user id", status: "error" });
        return null;
      }

      setLoadingScore(true);
      try {
        const req = {
          surveyId: surveyIdRef.current,
          score,
          userId,
        };

        console.info("[NPS] Submitting score:", req);
        const res = await dispatch(answerNps(userId, req));
        console.info("[NPS] Score submission FULL response:", res);

        // CAPTURE ALL FIELDS from backend response (if present)
        const backendData = res?.data || res;

        const bucket = getBucketFromScore(score);
        setSurveyData(prev => ({
          ...prev,
          mainScore: score,
          followupBucket: bucket,
          completedAt: backendData?.answeredAt || new Date().toISOString(),
        }));

        setSelectedScore(score);
        setFollowupQuestion(getFollowupQuestionFromBucket(bucket));

        toast({
          title: "Score submitted!",
          description: "Please provide additional feedback",
          status: "success",
          duration: 3000,
        });

        return res;
      } catch (err) {
        console.error("[NPS] submitScore error:", err);
        toast({ title: "Failed to submit score", status: "error" });
        return null;
      } finally {
        setLoadingScore(false);
      }
    },
    [dispatch, userId, toast, getBucketFromScore, getFollowupQuestionFromBucket]
  );

  // UPDATED: submitFollowup sets surveyCompletedRef to true and clears surveyIdRef
  const submitFollowup = useCallback(async () => {
    if (!userId || !surveyIdRef.current) {
      toast({ title: "Missing survey id or user", status: "warning" });
      return null;
    }
    if (!followupAnswer || !followupAnswer.trim()) {
      toast({ title: "Please enter feedback", status: "warning" });
      return null;
    }

    setLoadingFollowup(true);
    try {
      const payload = {
        surveyId: surveyIdRef.current,
        answer: followupAnswer,
        userId,
      };

      console.info("[NPS] Submitting followup:", payload);
      const resp = await fetch("/api/v1/nps/answer-followup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      // DEBUG: log status & headers
      console.info("[NPS] followup resp status:", resp.status, resp.statusText);
      console.info("[NPS] followup resp headers:", Object.fromEntries(resp.headers.entries()));

      // Read text first so we can handle HTML error pages without throwing during parsing
      const raw = await resp.text();
      const contentType = (resp.headers.get("content-type") || "").toLowerCase();

      // If the server returned JSON content-type, try to parse; otherwise log raw body and throw
      let data = null;
      if (contentType.includes("application/json")) {
        try {
          data = JSON.parse(raw);
        } catch (parseErr) {
          console.error("[NPS] Failed parsing JSON followup response:", parseErr, "raw:", raw);
          throw new Error("Invalid JSON returned from server");
        }
      } else {
        // Non-JSON (likely HTML). Log first 1000 chars for debugging.
        console.warn("[NPS] followup returned non-JSON body (first 1000 chars):", raw.slice(0, 1000));
        throw new Error(`Unexpected server response (content-type: ${contentType || "unknown"})`);
      }

      console.info("[NPS] Followup FULL response (parsed):", data);

      if (!resp.ok) {
        throw new Error(data?.error || `HTTP ${resp.status}`);
      }

      // Mark survey completed to prevent re-opening on subsequent pending checks
      surveyCompletedRef.current = true;

      // CAPTURE followupAnswer and any other data from followup response
      setSurveyData(prev => ({
        ...prev,
        followupAnswer: followupAnswer,
        completedAt: data?.completedAt || prev.completedAt,
      }));

      toast({
        title: "Thank you! Feedback saved successfully",
        description: `Your ${surveyData.followupBucket || getBucketFromScore(selectedScore)} feedback has been recorded`,
        status: "success",
        duration: 5000,
      });

      // Reset UI state and clear survey id so we don't re-submit
      setShowModal(false);
      setSelectedScore(null);
      setFollowupAnswer("");
      setFollowupQuestion("");
      surveyIdRef.current = null;

      return data;
    } catch (err) {
      console.error("[NPS] submitFollowup error:", err);

      const msg = (err && err.message) ? err.message : String(err);
      if (msg.includes("already completed") || msg.includes("Survey not ready")) {
        toast({
          title: "Survey Completed",
          description: "Your score has been submitted. Thank you!",
          status: "info",
        });
        setShowModal(false);
        // ensure we mark completed so future checks won't re-open
        surveyCompletedRef.current = true;
      } else {
        toast({ title: msg || "Failed to save feedback", status: "error" });
      }
      return null;
    } finally {
      setLoadingFollowup(false);
    }
  }, [followupAnswer, userId, toast, surveyData, selectedScore, getBucketFromScore]);

  const resetSurveyState = useCallback(() => {
    setSelectedScore(null);
    setFollowupAnswer("");
    setFollowupQuestion("");
    surveyIdRef.current = null;
    setMainQuestion("");
    setSurveyData({
      mainScore: null,
      followupBucket: "",
      completedAt: null,
      followupAnswer: ""
    });
    surveyCompletedRef.current = false;
  }, []);

  const handleManualTest = useCallback(async () => {
    if (!userId) {
      toast({ title: "User not logged in", status: "warning" });
      return;
    }

    console.info("[NPS] Manual test - checking for survey");
    await checkPendingSurvey();
  }, [userId, checkPendingSurvey, toast]);

  // ------------ useEffect ------------
  useEffect(() => {
    if (!userId) {
      console.info("[NPS] userId not ready yet - skipping start");
      return;
    }

    startUserSession();

    const onBeforeUnload = (e) => {
      endUserSession();
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      clearTimeout(autoEndTimerRef.current);
    };
  }, [userId, startUserSession, endUserSession]);

  // ------------ RENDER ------------
  if (!userId) return null;

  return (
    <>
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <VStack align="start" spacing={1}>
              <Text>Net Promoter Score (NPS)</Text>
              {surveyData.followupBucket && (
                <Badge colorScheme={getBucketColor(surveyData.followupBucket)}>
                  {surveyData.followupBucket.toUpperCase()}
                </Badge>
              )}
            </VStack>
          </ModalHeader>
          <ModalBody>
            <Text fontWeight="700" mb={3}>
              {mainQuestion}
            </Text>

            <HStack spacing={2} wrap="wrap" mb={3}>
              {Array.from({ length: 11 }).map((_, i) => (
                <Button
                  key={i}
                  onClick={() => submitScore(i)}
                  size="sm"
                  variant={selectedScore === i ? "solid" : "outline"}
                  isLoading={loadingScore && selectedScore === null}
                  colorScheme={i <= 6 ? "red" : i <= 8 ? "orange" : "green"}
                >
                  {i}
                </Button>
              ))}
            </HStack>

            {selectedScore !== null && (
              <Box mt={4}>
                <Text fontWeight="600" mb={2}>
                  {followupQuestion}
                </Text>

                <Textarea
                  placeholder="Type your feedback..."
                  value={followupAnswer}
                  onChange={(e) => setFollowupAnswer(e.target.value)}
                  rows={4}
                />

                {surveyData.followupAnswer && (
                  <Box mt={2} p={2} bg="green.50" borderRadius="md">
                    <Text fontSize="sm" fontWeight="600">Saved Feedback:</Text>
                    <Text fontSize="sm">{surveyData.followupAnswer}</Text>
                  </Box>
                )}
              </Box>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="teal"
              onClick={submitFollowup}
              isDisabled={!followupAnswer.trim()}
              isLoading={loadingFollowup}
            >
              Submit Feedback
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
